import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useMyProfile } from '@/hooks/use-profile';
import { apiFetch } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';

interface ProfileFormValues {
  name: string;
  location: string;
  seniority: string;
}

export function ProfileSettingsTab() {
  const { data, isLoading } = useMyProfile();
  const queryClient = useQueryClient();
  const profile = data?.data;

  const [form, setForm] = useState<ProfileFormValues>({
    name: '',
    location: '',
    seniority: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (profile) {
      setForm({
        name: profile.name ?? '',
        location: profile.location ?? '',
        seniority: profile.seniority ?? ''
      });
    }
  }, [profile]);

  const handleChange = (field: keyof ProfileFormValues, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      await apiFetch('/profiles/me', {
        method: 'PATCH',
        body: JSON.stringify({
          name: form.name.trim() || undefined,
          location: form.location.trim() || undefined,
          seniority: form.seniority.trim() || undefined
        })
      });
      setSaveSuccess(true);
      queryClient.invalidateQueries({ queryKey: ['my-profile'] });
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save profile');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        No profile found. Upload a CV to create your profile.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-1 text-sm font-semibold">Profile Information</h3>
        <p className="text-sm text-muted-foreground">Update your name and location details.</p>
      </div>

      <div className="space-y-4 rounded-lg border p-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label htmlFor="profile-name" className="text-sm font-medium">
              Full Name
            </label>
            <input
              id="profile-name"
              type="text"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Your full name"
              className="h-9 w-full rounded-md border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-ring"
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="profile-location" className="text-sm font-medium">
              Location
            </label>
            <input
              id="profile-location"
              type="text"
              value={form.location}
              onChange={(e) => handleChange('location', e.target.value)}
              placeholder="e.g. Stockholm, Sweden"
              className="h-9 w-full rounded-md border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-ring"
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="profile-seniority" className="text-sm font-medium">
              Seniority Level
            </label>
            <select
              id="profile-seniority"
              value={form.seniority}
              onChange={(e) => handleChange('seniority', e.target.value)}
              className="h-9 w-full rounded-md border bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="">Select level</option>
              <option value="junior">Junior</option>
              <option value="mid">Mid-level</option>
              <option value="senior">Senior</option>
              <option value="lead">Lead</option>
              <option value="principal">Principal</option>
              <option value="director">Director</option>
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Email</label>
            <input
              type="email"
              value={profile.email ?? ''}
              disabled
              className="h-9 w-full rounded-md border bg-muted px-3 text-sm text-muted-foreground"
            />
            <p className="text-xs text-muted-foreground">Managed by your identity provider</p>
          </div>
        </div>

        {saveError && <p className="text-sm text-destructive">{saveError}</p>}
        {saveSuccess && (
          <p className="text-sm text-green-600 dark:text-green-400">Profile saved successfully.</p>
        )}

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={isSaving} size="sm">
            {isSaving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}
